import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { launchesApi } from "../../api/endpoints";
import type { Launch, LaunchStatus } from "../../types/domain";
import { IcoCheck, IcoClose } from "../../components/icons";
import { ModalBackdrop } from "../../components/ModalBackdrop";
import { launchAvatarText } from "../../lib/launchAvatar";
import { isTouchLike } from "../../lib/device";
import { useCanWrite } from "../../hooks/useCanWrite";

const COLOR_SWATCHES = [
  "#5B5BD6",
  "#14B8A6",
  "#8B5CF6",
  "#F59E0B",
  "#EF4444",
  "#EC4899",
  "#6366F1",
  "#10B981",
  "#0EA5E9",
  "#7A849B",
];

const STATUS_OPTS: Array<{ v: LaunchStatus; l: string; hint: string }> = [
  { v: "draft", l: "Черновик", hint: "в планах" },
  { v: "active", l: "Активный", hint: "идёт сейчас" },
  { v: "completed", l: "Завершён", hint: "уже позади" },
  { v: "archived", l: "В архиве", hint: "не мешает в списке" },
];

const toDateInput = (iso: string): string => {
  if (!iso) return "";
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
};

interface Props {
  launch: Launch;
  onClose: () => void;
  onSaved?: (launch: Launch) => void;
}

export function EditLaunchModal({ launch, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const canWrite = useCanWrite();
  const readOnly = !canWrite;

  const [name, setName] = useState(launch.name);
  const [emoji, setEmoji] = useState(launch.emoji ?? "");
  const [color, setColor] = useState(launch.color ?? "#5B5BD6");
  const [status, setStatus] = useState<LaunchStatus>(launch.status);
  const [d1StartDate, setD1StartDate] = useState(toDateInput(launch.d1StartDate));
  const [goal, setGoal] = useState(launch.goal ?? "");
  const [audience, setAudience] = useState(launch.audience ?? "");
  const [description, setDescription] = useState(launch.description ?? "");
  const [result, setResult] = useState(launch.result ?? "");

  const initialDate = toDateInput(launch.d1StartDate);
  const dateChanged = d1StartDate !== initialDate && d1StartDate !== "";

  const patchMutation = useMutation({
    mutationFn: () => {
      const trimmedEmoji = emoji.trim();
      const patch: Partial<Launch> = {
        name: name.trim(),
        emoji: trimmedEmoji === "" ? null : trimmedEmoji,
        color,
        status,
        d1StartDate,
        goal: goal.trim() === "" ? null : goal.trim(),
        audience: audience.trim() === "" ? null : audience.trim(),
        description: description.trim() === "" ? null : description.trim(),
        result: result.trim() === "" ? null : result.trim(),
      };
      return launchesApi.patch(launch.id, patch);
    },
    onSuccess: ({ launch: fresh }) => {
      qc.setQueryData<Launch[]>(["launches"], (old) =>
        old ? old.map((l) => (l.id === fresh.id ? fresh : l)) : old,
      );
      qc.setQueryData<Launch>(["launch", launch.id], fresh);
      qc.invalidateQueries({ queryKey: ["launches"] });
      qc.invalidateQueries({ queryKey: ["launch", launch.id] });
      if (onSaved) onSaved(fresh);
      onClose();
    },
  });

  const canSave =
    name.trim().length > 0 &&
    d1StartDate !== "" &&
    /^#[0-9a-fA-F]{6}$/.test(color) &&
    emoji.length <= 4 &&
    !patchMutation.isPending;

  const save = () => {
    if (!canSave) return;
    patchMutation.mutate();
  };

  const previewName = name.trim() || launch.name;
  const previewText = launchAvatarText({ name: previewName, emoji });

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div className="launch-dot" style={{ background: color, color: "white" }}>
            {previewText}
          </div>
          <h2>{readOnly ? "Запуск" : "Настройки запуска"}</h2>
          <div className="spacer" />
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            <IcoClose size={16} />
          </button>
        </div>

        <div className="modal-body">
          <div className="form-row">
            <label className="form-label">Название</label>
            <input
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus={!readOnly && !isTouchLike()}
              maxLength={120}
              disabled={readOnly}
            />
          </div>

          <div className="form-row split">
            <div>
              <label className="form-label">Иконка / инициалы</label>
              <input
                className="form-input"
                value={emoji}
                onChange={(e) => setEmoji(e.target.value)}
                placeholder="Пусто → буквы из имени"
                maxLength={4}
                disabled={readOnly}
              />
              <div
                style={{
                  fontSize: 12,
                  color: "var(--text-3)",
                  marginTop: 6,
                  lineHeight: 1.4,
                }}
              >
                Можно emoji (🎤) или короткий текст («О8»). Оставьте пустым — возьмём инициалы из названия.
              </div>
            </div>
            <div>
              <label className="form-label">Цвет</label>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                {COLOR_SWATCHES.map((c) => {
                  const active = c.toLowerCase() === color.toLowerCase();
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      aria-label={`Цвет ${c}`}
                      disabled={readOnly}
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 999,
                        background: c,
                        border: active
                          ? "2px solid var(--text)"
                          : "2px solid transparent",
                        boxShadow: active
                          ? "0 0 0 2px var(--surface), 0 0 0 3px var(--border)"
                          : "inset 0 0 0 1px rgba(15,23,42,0.08)",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    />
                  );
                })}
                <label
                  title="Выбрать свой цвет"
                  style={{
                    position: "relative",
                    width: 26,
                    height: 26,
                    borderRadius: 999,
                    border: "2px dashed var(--border)",
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    background: "var(--surface-2)",
                    fontSize: 11,
                    color: "var(--text-3)",
                    overflow: "hidden",
                  }}
                >
                  ✚
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    disabled={readOnly}
                    style={{
                      position: "absolute",
                      inset: 0,
                      opacity: 0,
                      cursor: readOnly ? "default" : "pointer",
                    }}
                  />
                </label>
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--text-3)",
                    fontFamily: "JetBrains Mono, monospace",
                    marginLeft: 4,
                  }}
                >
                  {color.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          <div className="form-row">
            <label className="form-label">Статус</label>
            <div className="clone-row">
              {STATUS_OPTS.map((s) => (
                <button
                  key={s.v}
                  type="button"
                  className={"clone-opt" + (status === s.v ? " active" : "")}
                  onClick={() => !readOnly && setStatus(s.v)}
                  disabled={readOnly}
                >
                  <div>
                    <b>{s.l}</b>
                    <span>{s.hint}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="form-row split">
            <div>
              <label className="form-label">Дата старта эфиров</label>
              <input
                className="form-input"
                type="date"
                value={d1StartDate}
                onChange={(e) => setD1StartDate(e.target.value)}
                disabled={readOnly}
              />
              {dateChanged && (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--text-3)",
                    marginTop: 6,
                    lineHeight: 1.4,
                  }}
                >
                  ⚠ Все дни запуска сдвинутся относительно новой даты старта.
                </div>
              )}
            </div>
            <div>
              <label className="form-label">Аудитория</label>
              <input
                className="form-input"
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                placeholder="Например: прошлые ученики + тёплая база"
                maxLength={200}
                disabled={readOnly}
              />
            </div>
          </div>

          <div className="form-row">
            <label className="form-label">Цель</label>
            <input
              className="form-input"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="Например: 150 регистраций и 2 млн выручки"
              maxLength={200}
              disabled={readOnly}
            />
          </div>

          <div className="form-row">
            <label className="form-label">Описание</label>
            <textarea
              className="form-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Пара строк контекста — для будущего себя или команды"
              maxLength={2000}
              rows={3}
              style={{ resize: "vertical", minHeight: 72 }}
              disabled={readOnly}
            />
          </div>

          {(status === "completed" || launch.result) && (
            <div className="form-row">
              <label className="form-label">Результат</label>
              <input
                className="form-input"
                value={result}
                onChange={(e) => setResult(e.target.value)}
                placeholder="Например: 187 рег., 1.8 млн выручки"
                maxLength={200}
                disabled={readOnly}
              />
            </div>
          )}

          {patchMutation.error && (
            <div
              style={{
                color: "var(--text-danger, #DC2626)",
                fontSize: 12.5,
                marginTop: 8,
              }}
            >
              ⚠ {(patchMutation.error as Error).message}
            </div>
          )}
        </div>

        <div className="modal-foot">
          <div className="spacer" />
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={patchMutation.isPending}
          >
            {readOnly ? "Закрыть" : "Отмена"}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={!canSave}
            >
              <IcoCheck size={14} />
              {patchMutation.isPending ? "Сохраняем…" : "Сохранить"}
            </button>
          )}
        </div>
      </div>
    </ModalBackdrop>
  );
}
